import { KEYPAD_FAMILY_SPECS } from './catalog/family';
import type { KeypadFamilyKey } from './catalog/family';
import { KEY_LAYOUT_SPECS, keyNumberOfIndex } from './catalog/layout';
import type { KeyLayoutKey } from './catalog/layout';
import { KEY_STATES } from './catalog/keyState';
import type { KeyState } from './catalog/keyState';
import { KEYPAD_SCHEMA_VERSION } from './catalog/storage';
import { defaultKeyNameOf, familyOfType, layoutOfType } from './catalog/type';
import type { KeypadTypeKey } from './catalog/type';
import { newKeypadId, newKeypadKeyId } from './ids';
import type { KeypadId, KeypadKeyId } from './ids';

/** One output, at the value this key state gives it. */
export interface LoadState {
  /** A target id as the outside world gave it; the feature stores nothing else. */
  readonly targetId: string;
  /** Meaning depends on the target's value kind — see `catalog/value`. */
  readonly value: number;
}

/** What one key does when it is pressed, or released. */
export interface KeyStateMapping {
  readonly state: KeyState;
  readonly loads: readonly LoadState[];
}

/** One key on the panel face. */
export interface KeypadKey {
  readonly id: KeypadKeyId;
  /** 0-based position on the face; the trigger carries it counted from one. */
  readonly index: number;
  /** Engraving text. */
  readonly name: string;
  readonly states: readonly KeyStateMapping[];
}

/** A panel as the technician defined it. */
export interface Keypad {
  readonly id: KeypadId;
  readonly type: KeypadTypeKey;
  readonly name: string;
  /** null for a family whose keys carry their own fixed selectors. */
  readonly address: number | null;
  /** CAT-6 branch the fitter used; wiring information, never sent to the device. */
  readonly cat6Branch: number | null;
  /** Where the panel is fitted, in the technician's own words. */
  readonly location: string;
  readonly keys: readonly KeypadKey[];
  /**
   * Addresses this panel answered on before it was moved. The device still holds
   * whatever was written to them, so the console can offer to erase it.
   */
  readonly retiredAddresses: readonly number[];
  readonly createdAt: string;
}

/**
 * Every panel this tablet knows, plus the fingerprints of what it last wrote.
 *
 * `written` maps a trigger key (see `encode.ts`) to the canonical JSON of the
 * entry that was sent, which is how "edited since writing" is answered without
 * asking the device.
 */
export interface KeypadBook {
  readonly schemaVersion: number;
  readonly keypads: readonly Keypad[];
  readonly written: Readonly<Record<string, string>>;
  readonly updatedAt: string;
}

/** What a new panel needs before it exists. */
export interface KeypadDraft {
  readonly type: KeypadTypeKey;
  readonly name: string;
  readonly address: number | null;
  readonly cat6Branch: number | null;
  readonly location: string;
}

/** The family a panel belongs to, which decides its address rules. */
export const familyOf = (keypad: Keypad): KeypadFamilyKey => familyOfType(keypad.type);

/** The key layout a panel's type uses. */
export const layoutOf = (keypad: Keypad): KeyLayoutKey => layoutOfType(keypad.type);

/** The keys a type carries, named as the catalog names them. */
export const keysForType = (type: KeypadTypeKey): readonly KeypadKey[] =>
  KEY_LAYOUT_SPECS[layoutOfType(type)].cells.map((cell) => ({
    id: newKeypadKeyId(),
    index: cell.index,
    name: defaultKeyNameOf(type, cell.index),
    states: [],
  }));

/** A brand-new panel, its keys named and unmapped. */
export const createKeypad = (draft: KeypadDraft, now: string): Keypad => ({
  id: newKeypadId(),
  type: draft.type,
  name: draft.name,
  address: draft.address,
  cat6Branch: draft.cat6Branch,
  location: draft.location,
  keys: keysForType(draft.type),
  retiredAddresses: [],
  createdAt: now,
});

export const createEmptyBook = (now: string): KeypadBook => ({
  schemaVersion: KEYPAD_SCHEMA_VERSION,
  keypads: [],
  written: {},
  updatedAt: now,
});

/** The loads a key state drives; an unmapped state is empty, never undefined. */
export const loadsFor = (key: KeypadKey, state: KeyState): readonly LoadState[] =>
  key.states.find((mapping) => mapping.state === state)?.loads ?? [];

/** True when the key drives anything at all. */
export const isProgrammed = (key: KeypadKey): boolean =>
  KEY_STATES.some((state) => loadsFor(key, state).length > 0);

/** How many of a panel's keys drive anything. */
export const programmedKeyCount = (keypad: Keypad): number =>
  keypad.keys.filter(isProgrammed).length;

/** Replaces one key, leaving the others untouched. */
export const withKey = (
  keypad: Keypad,
  keyId: KeypadKeyId,
  update: (key: KeypadKey) => KeypadKey,
): Keypad => ({
  ...keypad,
  keys: keypad.keys.map((key) => (key.id === keyId ? update(key) : key)),
});

/**
 * Replaces one state mapping, or adds it when the key had none. States are kept
 * in catalog order so a saved book and a fingerprint are stable.
 */
export const withStateLoads = (
  key: KeypadKey,
  state: KeyState,
  loads: readonly LoadState[],
): KeypadKey => {
  const replaced = KEY_STATES.flatMap((candidate) => {
    if (candidate === state) return [{ state, loads }];
    const existing = key.states.find((mapping) => mapping.state === candidate);
    return existing === undefined ? [] : [existing];
  });
  return { ...key, states: replaced };
};

/** The number a key is shown by — the same number the firmware knows it as. */
export const keyNumber = (key: KeypadKey): number => keyNumberOfIndex(key.index);

/** The name to fall back on when a key has none of its own. */
export const keyDisplayName = (keypad: Keypad, key: KeypadKey): string => {
  const trimmed = key.name.trim();
  if (trimmed !== '') return trimmed;
  const fallback = defaultKeyNameOf(keypad.type, key.index);
  return fallback === '' ? `Button ${keyNumber(key)}` : fallback;
};

/** Panels in the order the console lists them: by address, then by name. */
export const sortedKeypads = (book: KeypadBook): readonly Keypad[] =>
  [...book.keypads].sort((left, right) => {
    const leftAddress = left.address ?? Number.MAX_SAFE_INTEGER;
    const rightAddress = right.address ?? Number.MAX_SAFE_INTEGER;
    if (leftAddress !== rightAddress) return leftAddress - rightAddress;
    return left.name.localeCompare(right.name);
  });

/** The family's caveat, when it has one worth showing. */
export const caveatOf = (keypad: Keypad): string | null =>
  KEYPAD_FAMILY_SPECS[familyOf(keypad)].caveat;
